import React, { useState, useRef, useEffect } from 'react';
import {
  Card, Input, Button, Typography, Space, Tag, Alert, Spin, Avatar, Popconfirm, message as antMessage,
} from 'antd';
import {
  SendOutlined, FacebookOutlined, CheckCircleOutlined,
  LinkOutlined, RobotOutlined, UserOutlined, VideoCameraOutlined,
  LoadingOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { miraAPI, videoAPI } from '../services/api';

const { Text, Paragraph } = Typography;

export default function MiraChatPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Check Mira status and load history on mount
  useEffect(() => {
    miraAPI.status().then(res => {
      const d = res.data || res;
      setConnected(d.ok);
    }).catch(() => setConnected(false));

    miraAPI.history().then(res => {
      const data = res.data || res;
      if (Array.isArray(data) && data.length > 0) {
        setMessages(data.map(m => ({
          role: m.role,
          content: m.content,
          actions: m.actions || undefined,
        })));
      }
    }).catch(() => {});
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      const raw = await miraAPI.chat({ message: text, conversation_history: history });
      const res = raw.data || raw;

      if (res.ok) {
        const botMsg = { role: 'assistant', content: res.reply, actions: res.actions };
        setMessages(prev => [...prev, botMsg]);

        // If any wav2lip action is processing, poll for completion
        if (res.actions) {
          res.actions.forEach((action, actionIdx) => {
            if (action.type === 'wav2lip' && action.result?.status === 'processing' && action.result?.job_id) {
              pollWav2lipJob(action.result.job_id, actionIdx);
            }
          });
        }
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: res.error || 'Mira 無法回應，請稍後再試',
          isError: true,
        }]);
      }
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '連線失敗，請確認網路或稍後再試',
        isError: true,
      }]);
    }
    setSending(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const pollWav2lipJob = async (jobId, actionIdx) => {
    while (true) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const pollRes = await videoAPI.getJobStatus(jobId);
        const job = pollRes?.data;
        if (!job) break;

        if (job.status === 'completed' || job.status === 'failed') {
          setMessages(prev => {
            const updated = [...prev];
            for (let i = updated.length - 1; i >= 0; i--) {
              if (updated[i].role === 'assistant' && updated[i].actions) {
                const msg = { ...updated[i], actions: [...updated[i].actions] };
                // Update wav2lip action
                if (msg.actions[actionIdx]) {
                  msg.actions[actionIdx] = {
                    ...msg.actions[actionIdx],
                    result: job.status === 'completed'
                      ? { ok: true, video_url: job.video_url, info: job.info, latency_ms: job.latency_ms }
                      : { ok: false, error: job.error || '影片生成失敗' },
                  };
                }
                // Update pending fb_video action with auto-post result
                if (job.fb_result) {
                  const fbIdx = msg.actions.findIndex(a => a.type === 'fb_video' && a.result?.status === 'pending_video');
                  if (fbIdx >= 0) {
                    msg.actions[fbIdx] = { ...msg.actions[fbIdx], result: job.fb_result };
                  }
                }
                updated[i] = msg;
                break;
              }
            }
            return updated;
          });
          break;
        }
      } catch (e) {
        break;
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleClearHistory = async () => {
    try {
      await miraAPI.clearHistory();
      setMessages([]);
      antMessage.success('對話紀錄已清除');
    } catch {
      antMessage.error('清除失敗');
    }
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Card
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          border: 'none', borderRadius: '16px 16px 0 0',
          flexShrink: 0,
        }}
        bodyStyle={{ padding: '16px 20px' }}
      >
        <Space size={12} align="center">
          <Avatar
            size={44}
            src="http://10.66.99.82:18799/chat/mira"
            style={{ background: 'linear-gradient(135deg, #f093fb, #f5576c)', fontSize: 20, fontWeight: 700 }}
          >
            M
          </Avatar>
          <div>
            <Text style={{ color: '#fff', fontSize: 18, fontWeight: 700, display: 'block' }}>Mira</Text>
            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>
              AI 助理 — 可幫你發 Facebook 貼文
            </Text>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tag
              color={connected === true ? 'green' : connected === false ? 'red' : 'default'}
            >
              {connected === true ? '在線' : connected === false ? '離線' : '...'}
            </Tag>
            {messages.length > 0 && (
              <Popconfirm title="確認清除所有對話紀錄？" onConfirm={handleClearHistory} okText="清除" cancelText="取消">
                <Button size="small" ghost icon={<DeleteOutlined />} style={{ color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.4)' }}>
                  清除紀錄
                </Button>
              </Popconfirm>
            )}
          </div>
        </Space>
      </Card>

      {/* Chat Messages */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '16px',
        background: '#f5f5f5', minHeight: 0,
      }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
            <RobotOutlined style={{ fontSize: 48, marginBottom: 16, color: '#764ba2' }} />
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Hi! I'm Mira</div>
            <div style={{ fontSize: 13, maxWidth: 400, margin: '0 auto', lineHeight: 1.8 }}>
              你可以跟我聊天，也可以請我幫你發 Facebook 貼文。
              <br />試試看：
            </div>
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
              {[
                '幫我寫一篇 Facebook 貼文介紹 SPO 行銷平台',
                '發一篇 FB 貼文：今天天氣真好！',
                '你好 Mira，你可以做什麼？',
              ].map((hint, i) => (
                <Button
                  key={i} size="small" type="dashed"
                  style={{ maxWidth: 360, width: '100%', textAlign: 'left', whiteSpace: 'normal', height: 'auto', padding: '6px 12px' }}
                  onClick={() => { setInput(hint); inputRef.current?.focus(); }}
                >
                  {hint}
                </Button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} style={{
            display: 'flex',
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            marginBottom: 12,
          }}>
            <div style={{
              maxWidth: '85%',
              display: 'flex',
              gap: 8,
              flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
              alignItems: 'flex-start',
            }}>
              <Avatar
                size={32}
                style={{
                  background: msg.role === 'user'
                    ? 'linear-gradient(135deg, #1890ff, #096dd9)'
                    : 'linear-gradient(135deg, #f093fb, #f5576c)',
                  flexShrink: 0,
                }}
                icon={msg.role === 'user' ? <UserOutlined /> : undefined}
              >
                {msg.role === 'assistant' && 'M'}
              </Avatar>
              <div>
                <div style={{
                  padding: '10px 14px',
                  borderRadius: msg.role === 'user' ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                  background: msg.role === 'user' ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#fff',
                  color: msg.role === 'user' ? '#fff' : msg.isError ? '#ff4d4f' : '#333',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: 14,
                  lineHeight: 1.6,
                }}>
                  {msg.content}
                </div>

                {/* Action results */}
                {msg.actions && msg.actions.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {msg.actions.map((action, ai) => {
                      const isPending = action.result?.status === 'processing' || action.result?.status === 'pending_video';
                      return (
                      <Card key={ai} size="small" style={{
                        borderRadius: 12,
                        border: isPending ? '1px solid #91caff'
                          : action.result?.ok ? '1px solid #b7eb8f' : '1px solid #ffa39e',
                        background: isPending ? '#e6f4ff'
                          : action.result?.ok ? '#f6ffed' : '#fff2f0',
                        marginBottom: 4,
                      }}>
                        {isPending ? (
                          <Space>
                            <LoadingOutlined style={{ color: '#1677ff' }} />
                            <Text style={{ color: '#1677ff' }}>
                              {action.result?.status === 'pending_video'
                                ? <><FacebookOutlined /> 等待影片生成完成後自動發布...</>
                                : <><VideoCameraOutlined /> 影片生成中...（約1-2分鐘）</>}
                            </Text>
                          </Space>
                        ) : action.result?.ok ? (
                          <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Space>
                              <CheckCircleOutlined style={{ color: '#52c41a' }} />
                              <Text strong style={{ color: '#52c41a' }}>
                                {action.type === 'fb_post' ? 'Facebook 貼文已發布!'
                                  : action.type === 'wav2lip' ? 'AI 影片生成完成!'
                                  : action.type === 'fb_video' ? '影片已發布到 Facebook!'
                                  : 'Blog 已發布到 Facebook!'}
                              </Text>
                            </Space>
                            {action.result.video_url && action.type === 'wav2lip' && (
                              <video controls style={{ width: '100%', maxHeight: 200, borderRadius: 8 }}
                                src={action.result.video_url} />
                            )}
                            {action.result.post_url && (
                              <Button
                                type="link" size="small" icon={<LinkOutlined />}
                                href={action.result.post_url} target="_blank"
                                style={{ padding: 0 }}
                              >
                                {action.type === 'fb_video' ? '查看影片' : '查看貼文'}
                              </Button>
                            )}
                          </Space>
                        ) : (
                          <Space>
                            <FacebookOutlined style={{ color: '#ff4d4f' }} />
                            <Text type="danger">{action.result?.error || '發布失敗'}</Text>
                          </Space>
                        )}
                      </Card>
                    );})}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {sending && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
            <Avatar size={32} style={{ background: 'linear-gradient(135deg, #f093fb, #f5576c)', flexShrink: 0 }}>M</Avatar>
            <div style={{ padding: '10px 14px', background: '#fff', borderRadius: '4px 16px 16px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <Spin size="small" /> <Text type="secondary" style={{ marginLeft: 8 }}>Mira 思考中...</Text>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div style={{
        padding: '12px 16px',
        background: '#fff',
        borderTop: '1px solid #f0f0f0',
        borderRadius: '0 0 16px 16px',
        flexShrink: 0,
      }}>
        {connected === false && (
          <Alert type="error" message="Mira 離線中，請稍後再試" showIcon style={{ marginBottom: 8, borderRadius: 8 }} banner />
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <Input.TextArea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="輸入訊息...（Enter 發送，Shift+Enter 換行）"
            autoSize={{ minRows: 3, maxRows: 8 }}
            style={{ borderRadius: 16, padding: '10px 16px', fontSize: 15, resize: 'none' }}
            disabled={sending || connected === false}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={sendMessage}
            loading={sending}
            disabled={!input.trim() || connected === false}
            style={{
              height: 44, width: 44, borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              border: 'none', flexShrink: 0,
            }}
          />
        </div>
      </div>
    </div>
  );
}
